// ============================================
// COMPANY EDIT PAGE — Sadece yönetimsel aksiyonlar
// ============================================

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ArrowLeft, Save, ShieldCheck, Building2, Globe, MapPin } from 'lucide-react';
import { PageHeader, ConfirmDialog } from '@/components';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/companies/$id/edit')({
  component: CompanyEditPage,
});

function CompanyEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isVerified, setIsVerified] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: companyData, isLoading } = useQuery({
    queryKey: ['admin', 'companies', id],
    queryFn: () => adminApi.getCompany(id),
  });

  const company = (companyData as any)?.data || companyData;

  useEffect(() => {
    if (company) {
      setIsVerified(company.isVerified || false);
    }
  }, [company]);

  // Doğrulama güncelleme mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => adminApi.updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      navigate({ to: `/companies/${id}` });
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  // Durum güncelleme mutation
  const statusMutation = useMutation({
    mutationFn: ({ status, reason }: { status: string; reason?: string }) =>
      adminApi.updateUserStatus(company?.userId, { status: status as any, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      navigate({ to: `/companies/${id}` });
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const handleSaveVerification = () => {
    updateMutation.mutate({ isVerified });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  // Mevcut kullanıcı durumunu al
  const userStatus = (company as any)?.user?.status || 'ACTIVE';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Şirket Yönetimi"
        description={company?.name}
        actions={
          <Link
            to={`/companies/${id}` as any}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Geri
          </Link>
        }
      />

      {/* Şirket bilgi özeti (read-only) */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Şirket Bilgileri</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Şirket Adı</p>
              <p className="font-medium text-gray-900">{company?.name || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Konum</p>
              <p className="font-medium text-gray-900">
                {company?.city && `${company.city}, `}{company?.country || '-'}
              </p>
            </div>
          </div>
          {company?.website && (
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Website</p>
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="font-medium text-maritime-600 hover:underline">
                  {company.website}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Doğrulama yönetimi */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Doğrulama Durumu</h3>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-maritime">
          <div className="flex items-center gap-3">
            <ShieldCheck className={cn('h-6 w-6', isVerified ? 'text-green-500' : 'text-gray-400')} />
            <div>
              <p className="font-medium text-gray-900">Doğrulanmış Şirket</p>
              <p className="text-sm text-gray-500">
                {isVerified ? 'Bu şirket doğrulanmış durumda' : 'Bu şirket henüz doğrulanmamış'}
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isVerified}
            onClick={() => setIsVerified(!isVerified)}
            className={cn(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              isVerified ? 'bg-green-500' : 'bg-gray-300'
            )}
          >
            <span className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              isVerified ? 'translate-x-6' : 'translate-x-1'
            )} />
          </button>
        </div>

        {errors.submit && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-maritime">
            <p className="text-sm text-red-600">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSaveVerification}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {updateMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Hesap durumu yönetimi */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Hesap Durumu</h3>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-maritime">
          <div>
            <p className="font-medium text-gray-900">Mevcut Durum</p>
            <p className={cn(
              'text-sm font-medium mt-1',
              userStatus === 'ACTIVE' && 'text-green-600',
              userStatus === 'SUSPENDED' && 'text-red-600',
              userStatus === 'INACTIVE' && 'text-gray-500',
            )}>
              {userStatus === 'ACTIVE' ? 'Aktif' : userStatus === 'SUSPENDED' ? 'Askıya Alındı' : 'Pasif'}
            </p>
          </div>
          <div className="flex gap-2">
            {userStatus === 'SUSPENDED' ? (
              <button
                onClick={() => statusMutation.mutate({ status: 'ACTIVE' })}
                disabled={statusMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-maritime hover:bg-green-700 disabled:opacity-50 text-sm"
              >
                Aktifleştir
              </button>
            ) : (
              <button
                onClick={() => statusMutation.mutate({ status: 'SUSPENDED' })}
                disabled={statusMutation.isPending}
                className="px-4 py-2 bg-amber-600 text-white rounded-maritime hover:bg-amber-700 disabled:opacity-50 text-sm"
              >
                Askıya Al
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
